import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Typography, Popconfirm, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { Store } from '../../types';

const { Title } = Typography;

const StoreList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchText, setSearchText] = useState('');

  // 獲取店鋪數據
  const fetchStores = async () => {
    setLoading(true);
    try {
      const data = await api.stores.getAll();
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  // 刪除店鋪
  const handleDelete = async (id: string) => {
    try {
      await api.stores.delete(id);
      message.success(t('common.success'));
      fetchStores();
    } catch (error) {
      console.error('Error deleting store:', error);
      message.error(t('common.error'));
    }
  };

  // 根據店鋪類型獲取標籤顏色
  const getTypeColor = (type: string): string => {
    switch (type) {
      case '自用':
        return 'blue';
      case '月租':
        return 'green';
      case '季度':
        return 'purple';
      default:
        return 'default';
    }
  };

  // 根據店鋪狀態獲取標籤顏色
  const getStatusColor = (status: string): string => {
    switch (status) {
      case '營業中':
        return 'success';
      case '停業':
        return 'warning';
      case '失效':
        return 'error';
      default:
        return 'default';
    }
  };

  // 表格列定義
  const columns: ColumnType<Store>[] = [
    {
      title: t('stores.storeId'),
      dataIndex: 'store_id',
      key: 'store_id',
      sorter: (a: Store, b: Store) => a.store_id.localeCompare(b.store_id),
    },
    {
      title: t('stores.platform'),
      dataIndex: 'platform',
      key: 'platform',
      filters: Array.from(new Set(stores.map(store => store.platform))).map((platform: string) => ({
        text: platform,
        value: platform,
      })),
      onFilter: (value: string | number | boolean, record: Store) => record.platform === value,
    },
    {
      title: t('stores.name'),
      dataIndex: 'name',
      key: 'name',
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value: string | number | boolean, record: Store) => {
        const searchValue = String(value).toLowerCase();
        return record.name.toLowerCase().includes(searchValue) ||
          (record.short_name ? record.short_name.toLowerCase().includes(searchValue) : false) ||
          record.store_id.toLowerCase().includes(searchValue);
      },
    },
    {
      title: t('stores.shortName'),
      dataIndex: 'short_name',
      key: 'short_name',
    },
    {
      title: t('stores.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type}</Tag>
      ),
      filters: [
        { text: t('stores.types.self'), value: '自用' },
        { text: t('stores.types.monthly'), value: '月租' },
        { text: t('stores.types.quarterly'), value: '季度' },
      ],
      onFilter: (value: string | number | boolean, record: Store) => record.type === value,
    },
    {
      title: t('stores.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
      filters: [
        { text: t('stores.statuses.operating'), value: '營業中' },
        { text: t('stores.statuses.suspended'), value: '停業' },
        { text: t('stores.statuses.expired'), value: '失效' },
      ],
      onFilter: (value: string | number | boolean, record: Store) => record.status === value,
    },
    {
      title: t('stores.owner'),
      dataIndex: 'owner',
      key: 'owner',
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_: any, record: Store) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/stores/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/stores/${record.id}/edit`)}
          />
          <Popconfirm
            title={t('stores.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>{t('stores.title')}</Title>
        <Space>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/stores/new')}
          >
            {t('stores.addStore')}
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={stores}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default StoreList;
